import { useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Divider, Drawer, IconButton, List } from '@material-ui/core';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LockClockIcon from '@mui/icons-material/LockClock';
import OverviewIcon from '@mui/icons-material/DonutSmall';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { PersonSearch as PersonSearchIcon } from '@material-ui/icons';
import { DashboardSidebarItem } from './dashboard-sidebar-item';
import { Scrollbar } from './scrollbar';
import { ChartBar as ControlTowerIcon } from '../icons/chart-bar';
import { ChevronLeft as ChevronLeftIcon } from '../icons/chevron-left';
import { ChevronRight as ChevronRightIcon } from '../icons/chevron-right';
import { UserGroup as GroupsIcon } from '../icons/user-group';
import { CustomChartPie as OptimizerGroupsIcon } from '../icons/custom-chart-pie';
import { ChartSquareBar as OptimizationsIcon } from '../icons/chart-square-bar';
import { OfficeBuilding as DepotsIcon } from '../icons/office-building';
import { Star } from '../icons/star';

const items = [
  {
    icon: ControlTowerIcon,
    title: 'Control tower',
    href: '/'
  },
  {
    icon: OverviewIcon,
    title: 'Daily overview',
    href: '/overview'
  },
  {
    icon: OptimizationsIcon,
    title: 'Optimizations',
    href: '/optimizations'
  },
  {
    icon: OptimizerGroupsIcon,
    title: 'Optimizer groups',
    href: '/optimizer-groups'
  },
  {
    icon: GroupsIcon,
    title: 'Groups',
    href: '/groups'
  },
  {
    icon: LockClockIcon,
    title: 'Postal Code Ranges',
    href: '/postal-code-ranges'
  },
  {
    icon: DepotsIcon,
    title: 'Depots',
    href: '/depots'
  },
  {
    icon: LocalShippingIcon,
    title: 'Vehicles',
    items: [
      {
        href: '/vehicles',
        title: 'List'
      },
      {
        href: '/vehicles/overview',
        title: 'Overview'
      }
    ]
  },
  {
    icon: LockClockIcon,
    title: 'Time Windows',
    href: '/time-windows'
  },
  {
    icon: PictureAsPdfIcon,
    title: 'Utils',
    href: '/utils'
  },
  {
    icon: PersonSearchIcon,
    title: 'Drivers',
    href: '/drivers'
  },
  {
    icon: AccountCircle,
    title: 'Users',
    href: '/users'
  },
  {
    icon: ShoppingCartIcon,
    title: 'MyFetch',
    items: [
      {
        href: '/postcodes',
        title: 'Postcodes'
      }
    ]
  },
  {
    icon: Star,
    title: 'Job events',
    href: '/job-events'
  },
  {
    icon: DepotsIcon,
    title: 'Geomapper',
    href: '/geomapper'
  }
];

export const DashboardSidebar = (props) => {
  const { onPin, pinned } = props;
  const { pathname } = useLocation();
  const [openedItem, setOpenedItem] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [activeHref, setActiveHref] = useState('');
  const [hovered, setHovered] = useState(false);

  const handleOpenItem = (item) => {
    if (openedItem === item) {
      setOpenedItem(null);
      return;
    }

    setOpenedItem(item);
  };

  useEffect(() => {
    items.forEach((item) => {
      if (item.items) {
        for (let index = 0; index < item.items.length; index++) {
          const active = matchPath({ path: item.items[index].href, end: true }, pathname);

          if (active) {
            setActiveItem(item);
            setActiveHref(item.items[index].href);
            setOpenedItem(item);
            break;
          }
        }
      } else {
        const active = !!matchPath({ path: item.href, end: true }, pathname);

        if (active) {
          setActiveItem(item);
          setOpenedItem(item);
        }
      }

      if (!activeItem) {
        const active = pathname.includes(item.href);
        if (active) {
          setActiveItem(item);
          setOpenedItem(item);
        }
      }
    });
  }, [pathname]);

  return (
    <Drawer
      open
      sx={{ zIndex: 1000 }}
      variant="permanent"
      PaperProps={{
        onMouseOver: () => { setHovered(true); },
        onMouseLeave: () => { setHovered(false); },
        sx: {
          backgroundColor: 'background.paper',
          height: 'calc(100% - 64px)',
          overflowX: 'hidden',
          top: 64,
          transition: 'width 250ms ease-in-out',
          width: pinned ? 270 : 73,
          '& .simplebar-content': {
            height: '100%'
          },
          '&:hover': {
            width: 270,
            '& span, p': {
              display: 'flex'
            }
          }
        }
      }}
    >
      <Scrollbar
        style={{
          display: 'flex',
          flex: 1,
          overflowX: 'hidden',
          overflowY: 'auto'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 2
          }}
        >
          <List disablePadding>
            {items.map((item) => (
              <DashboardSidebarItem
                active={activeItem?.title === item.title}
                activeHref={activeHref}
                key={item.title}
                onOpen={() => handleOpenItem(item)}
                open={openedItem?.title === item.title && (hovered || pinned)}
                pinned={pinned}
                {...item}
              />
            ))}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <Box sx={{ pt: 1 }}>
            <IconButton onClick={onPin}>
              {pinned ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
        </Box>
      </Scrollbar>
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onPin: PropTypes.func,
  pinned: PropTypes.bool
};
